import React, {useEffect} from "react";
import LinkScroll from "@/components/LinkScroll";
import styles from "@/components/TableOfContents.module.css";
import useActiveSection from "@/utils/ActiveSection";

export default function TableOfContents({headingData}) {
  const sectionIds = headingData.flatMap(heading => [heading.slug, ...heading.subheadings.map(sub => sub.slug)]);
  const activeSection = useActiveSection(sectionIds, '130px');

  const sectionRefs = headingData.reduce((refs, heading) => {
    // include heading
    refs[heading.slug] = React.createRef();
    // include subheadings
    heading.subheadings.forEach(subheading => {
      refs[subheading.slug] = React.createRef();
    });
    return refs;
  }, {});

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && entry.boundingClientRect.top < 130 && sectionRefs[entry.target.id]) {
            sectionRefs[entry.target.id].current = entry.target;
          }
        });
      },
      {rootMargin: "-130px 0px 0px 0px"}
    );

    const sectionElements = Object.values(sectionRefs);
    sectionElements.forEach((ref) => {
      if (ref.current) observer.observe(ref.current);
    });

    return () => {
      if (observer) {
        sectionElements.forEach((ref) => {
          if (ref.current) observer.unobserve(ref.current);
        });
      }
    };
  }, [sectionRefs]);

  return (
    <div className={styles.tableOfContents}>
      {headingData.map((heading) => (
        <div className={styles.contentRow} key={heading.slug}>
          <LinkScroll href={`#${heading.slug}`}>
            <div
              ref={sectionRefs[heading.slug]}
              className={
                activeSection === heading.slug
                  ? `${styles.active} ${styles[`level${heading.level}`]}`
                  : `${styles[`level${heading.level}`]}`
              }
            >
              {heading.text}
            </div>
          </LinkScroll>
          {heading.subheadings.length > 0 && (
            <div>
              {heading.subheadings.map((subheading) => (
                <LinkScroll href={`#${subheading.slug}`} key={subheading.slug}>
                  <div
                    ref={sectionRefs[subheading.slug]}
                    className={
                      activeSection === subheading.slug
                        ? `${styles.active} ${styles[`level${subheading.level}`]}`
                        : `${styles[`level${subheading.level}`]}`
                    }
                  >
                    {subheading.text}
                  </div>
                </LinkScroll>
              ))}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}
